import _ from "lodash";
import React, { useEffect } from 'react';
import styled from "styled-components";
import { Article } from "@/app/ui/Common/NewsList";
import { LANGTYPE_EN, useLanguage } from "@/app/utils/LanguageContext";
import { useMyContext } from "@/app/utils/MyContext";
import { getMeetingRaceNews } from "@/app/utils/request/requestList";
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { openTarget, getSearchParams, setRacesDetailLoading } from "@/app/utils/Common";
import { textSize, text_size } from "@/app/utils/MixinSize";
import NoInformation from "@/app/ui/Common/NoInformation";
import { dateFormat, formattedDate, toDate } from "@/app/utils/DayJS";

const NewsTitle = styled.div`
  width: 100%;
  height: 36px;
  line-height: 16px;
  padding: 10px 12px;
  ${text_size(15)}
  font-weight: 500;
  background-color: #EDEDED;
  box-shadow: 0px 2px 10px 0px rgb(0 0 0 / 10%);
`;

const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.root}`]: {
        color: "#333333",
    },
    [`&.${tableCellClasses.body}`]: {
        color: "#333333",
        ...textSize(13),
        fontWeight: 500,
        padding: "4px 0px 4px 8px",
        verticalAlign: "baseline",
        textAlign: "left",
        height: "32px",
        lineHeight: "24px"
    },
}));

const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(even)': {
        backgroundColor: "#F4F4F4",
    },
    '&:nth-of-type(odd)': {
        backgroundColor: "#FFFFFF",
    },
    'td, th': {
        border: 0,
    },
    'td:last-child, th:last-child': {
        paddingRight: "8px",
    },
    'td: first-child': {
        width: "14%",
        paddingRight: "8px",
        paddingLeft: "15px",
    }
}));

const StyledLink = styled(Link)(() => ({
    [`&.MuiLink-root`]: {
        ...textSize(13),
        color: "#122C68",
        textDecoration: "none",
        cursor: "pointer",
        "&:hover": {
            textDecoration: "underline",
            textDecorationColor: "#122C68"
        }
    }
}));

const ViewMoreBtn = styled.div`
    height: 32px;
    color: #122C68;
    ${text_size(13)}
    font-weight: 700;
    cursor: pointer;
    padding: 8px;
    justify-content: center;
    display: flex;
    align-items: center;
`;

interface propType {
  setRacesDetailLoading: (loading: boolean) => void;
    type?: string;
}

const tableRowDisplayCount = 15;

const News: React.FC<propType> = (props: propType) => {
    const { state: myContextState } = useMyContext();
    const [displayData, setDisplayData] = React.useState<Article[]>([]);
    const [fullNewsData, setFullNewsData] = React.useState<Article[]>([]);
    const [isExpanded, setIsExpanded] = React.useState(false);
    const { b_cid } = getSearchParams(new URLSearchParams(window.location.search));

    const { state: lang } = useLanguage();
    const { t } = lang;
    useEffect(() => {
        console.log('[News-----]', JSON.parse(JSON.stringify(myContextState)))
    }, [])
    useEffect(() => {
        console.log('[News-----2]', JSON.parse(JSON.stringify(myContextState)))
        if (!myContextState.selectedRace?.id || !myContextState.selectedMeeting?.id) {
            setRacesDetailLoading(props.setRacesDetailLoading)
            return
        }
        const raceNewsProm = new Promise((resolve, reject) => {
            getMeetingRaceNews(lang.langCode, myContextState.selectedRace?.id.toLowerCase() || "")
                .then((res: any) => {
                    resolve(res.search?.results?.items || [])
                })
                .catch((e: any) => {
                    console.log(e);
                    reject();
                });
        })

        const meetingNewsProm = new Promise((resolve, reject) => {
            getMeetingRaceNews(lang.langCode, myContextState.selectedMeeting?.id.toLowerCase() || "")
                .then((res: any) => {
                    resolve(res.search?.results?.items || [])
                })
                .catch((e: any) => {
                    console.log(e);
                    reject();
                });
        })

        Promise.all([raceNewsProm, meetingNewsProm]).then((res: any) => {
            const orderedNews = _.orderBy(res[0].concat(res[1]), ['item.publishDateTime.value'], ['desc'])
            setFullNewsData(orderedNews);
            setDisplayData(orderedNews.length > tableRowDisplayCount ? orderedNews.slice(0, tableRowDisplayCount) : orderedNews)
        }).finally(() => {
            setRacesDetailLoading(props.setRacesDetailLoading)
        })
    }, [myContextState.selectedRace, myContextState.selectedMeeting]);

    const handleViewMore = () => {
        setDisplayData(isExpanded ? fullNewsData.slice(0, tableRowDisplayCount) : fullNewsData);
        setIsExpanded(!isExpanded);
    }

    const convertDateTime = (value: string = "") => formattedDate(toDate(value, dateFormat.cms.datetime), "DD/MM/YYYY") || ""

    return (
        <>
            {props.type === "pc" ? (
                <div>
                    <NewsTitle id="newsTabTitle">{t("news_title")}</NewsTitle>
                    {
                        fullNewsData.length ?
                            <TableContainer id="newsTabTable" sx={{ display: "flex", borderRadius: "0px 0px 4px 4px", boxShadow: "0px 2px 0px 0px rgb(0 0 0 / 10%)" }}>
                                <Table sx={{ width: "100%" }} aria-label="simple table">
                                    <TableBody>
                                        {fullNewsData.map((row, index) => (
                                            <StyledTableRow key={`newsRow_${index}`} id={`newsRow_${index}`}>
                                                <StyledTableCell>
                                                    {convertDateTime(row.item?.publishDateTime?.value)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {
                                                        row.item?.url ?
                                                            <StyledLink href={row.item?.url.value || ""} target={openTarget(row.item?.target.value)}>{row.item?.title?.value}</StyledLink> :
                                                            <p>{row.item?.title?.value}</p>
                                                    }
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            :
                            <NoInformation />
                    }
                </div>
            ) : (
                fullNewsData.length ?
                    <div style={{ borderRadius: "0px 0px 4px 4px", boxShadow: "0px 2px 0px 0px rgb(0 0 0 / 10%)" }}>
                        <TableContainer id="newsTabMobileTable" sx={{ display: "flex" }}>
                            <Table sx={{ width: "100%" }} aria-label="simple table">
                                <TableBody>
                                    {displayData.map((row, index) => (
                                        <StyledTableRow key={`newsRow_${index}`} id={`newsRow_${index}`}>
                                            <StyledTableCell>
                                                {convertDateTime(row.item?.publishDateTime?.value)}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {
                                                    row.item?.url ?
                                                        <StyledLink href={row.item?.url.value || ""} target="_blank">{row.item?.title?.value}</StyledLink> :
                                                        <p>{row.item?.title?.value}</p>
                                                }
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {
                            fullNewsData.length > tableRowDisplayCount ?
                                <ViewMoreBtn id="newsTabExpandBtn" onClick={handleViewMore}>
                                    <span>{isExpanded ? t("viewLess") : t("viewMore")}</span>
                                    <img src={`${myContextState.assetPrefix}/footer/arrow-up.svg`} style={isExpanded ? { transform: "rotate(0)" } : { transform: "rotate(-180deg)" }} />
                                </ViewMoreBtn>
                                : <></>
                        }
                    </div>
                    :
                    <NoInformation />
            )
            }
        </>
    )
}

export default News; 